import React from 'react';

const GAME_URL = 'https://farshore.netlify.app/';

const post = {
  slug: 'farshore',
  title: 'Farshore: a first-person game with no game engine',
  date: '2026-09-13',
  cover: '/blog/farshore/conservatory.jpg',
  coverAlt:
    'A collapsed hall with a pool of rainwater under a wound in the roof, a light shaft falling onto the water, and a barred gate ahead',
  coverCaption: 'The collapsed hall. Something green glows behind that gate.',
  excerpt:
    'How we built Farshore, a first-person exploration game that runs free in your browser, using Three.js and no engine: every system hand-rolled, every sound synthesized.',
  content: (
    <>
      <p>
        <em>
          Update, 24 September 2026: Farshore has grown since this post. It is
          now eight chapters and a coda, about 45 to 60 minutes, with a
          generative score that is still synthesized in the browser, so still
          no audio files. It is in open playtest through October:{' '}
          <a href="/blog/farshore-playtest">play Farshore with us</a>.
        </em>
      </p>

      <figure className="post-figure">
        <div className="post-video">
          <iframe
            src="https://www.youtube-nocookie.com/embed/wipuulcLxms"
            title="Farshore trailer"
            loading="lazy"
            allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
          />
        </div>
        <figcaption>The trailer, rendered and recorded from the game itself.</figcaption>
      </figure>
      <p>
        <a href={GAME_URL}>Farshore</a> is a first-person exploration game that
        runs entirely in your browser. You wake in a brick tunnel inside a
        sealed, overgrown Conservatory. Ahead of you is a collapsed hall, a
        pool of rainwater under a wound in the roof, and something green
        glowing behind a barred gate. It takes about twenty minutes to play
        and it is free. But the part we are proudest of is what is not in it:
        no game engine, no asset packs, no audio files.
      </p>

      <h2>First principles</h2>
      <p>
        The stack is Three.js, TypeScript and Vite. Everything else is
        hand-rolled, mostly in a few hundred lines per system: the character
        controller, the interaction system, the doors, the procedural
        textures, the audio synthesis, most of the art.
      </p>
      <p>
        Take the controller. It works on acceleration, not velocity, so
        movement has weight. Collision is axis-separated boxes, which gives
        you wall sliding for free. Step-up logic makes stairs, roots and kerbs
        walkable without thinking about them. And a single stride clock drives
        both the camera&apos;s head-bob and the footsteps, so your eyes and
        ears can never drift apart. None of this is exotic. It is just careful
        work, and it is the difference between a demo and a game that feels
        good to walk around in.
      </p>
      <p>
        The audio is synthesized from nothing. Wind is filtered noise on a
        slow random walk. Footsteps are shaped bursts that turn to splashes
        when you wade. There is a moment near the end where two chimes play a
        few hundred milliseconds apart, slightly detuned, and the slow beat
        between them is two lights finding each other. That is the whole of
        the story in one sound, and it cost no audio files at all.
      </p>
      <p>
        Even the inventory icons are rendered live from the actual item
        meshes, so a thumbnail can never lie about what you are carrying.
      </p>

      <h2>Twenty minutes, four chapters</h2>
      <p>
        The game is a short, complete arc. The Conservatory gives way to an
        open shore with a glinting path across the water, then to a squat
        stone beacon you enter through its collapsed roof and walk down, and
        finally back to the shore changed. Each space inverts the one before
        it: bright becomes dim, horizontal becomes vertical, gold becomes
        teal. The story is told through objects, journals and a warden who
        stayed behind when everyone else was recalled, and it pays off if you
        are kind in the small way the game asks of you.
      </p>

      <figure className="post-figure">
        <img
          src="/blog/farshore/root-court.jpg"
          alt="A great tree in a circular stone court, a shaft of light rising from its canopy toward a wound in the ceiling"
        />
        <figcaption>The specimen. It does not drink the light; it makes it.</figcaption>
      </figure>

      <div className="post-photo-grid">
        <img
          src="/blog/farshore/shore.jpg"
          alt="An open shore with a skiff at a pier and a squat stone tower across the water"
        />
        <img
          src="/blog/farshore/beacon.jpg"
          alt="Looking down the beacon tower's spiral to a flooded floor where a small tree stands in the water"
        />
      </div>

      <p>
        The design rules it runs on are the reusable part. Show the
        destination long before it is reachable. Guide with light, because
        players walk toward light. Make the world remember what the player
        did, because reactivity is cheap and it is what makes a place feel
        alive. And keep text honest: if the words and the physics disagree,
        players believe the words.
      </p>

      <figure className="post-figure">
        <img
          src="/blog/farshore/lens.jpg"
          alt="A brass lens held up to an arrow slit, focusing a thin thread of light"
        />
        <figcaption>Kindness carried, not given.</figcaption>
      </figure>

      <figure className="post-figure">
        <img
          src="/blog/farshore/return.jpg"
          alt="The beacon tower across bright water, a warm shaft of light rising from it"
        />
        <figcaption>It was always answering. You just had to carry the light.</figcaption>
      </figure>

      <h2>Go and play it</h2>
      <p>
        If any of that sounds like your kind of thing,{' '}
        <a href={GAME_URL}>play Farshore here</a>. WASD to move, mouse to
        look, E to interact, Space to jump. Find the key, water the seedling,
        and mind the light. It is not tame.
      </p>
      <p>
        Farshore also plays right on its{' '}
        <a href="https://christopherlongden.itch.io/farshore">itch.io page</a>,
        where you can follow it as it heads for release.
      </p>
      <p>
        <em>
          Writing about Farshore? The{' '}
          <a href="https://farshore.netlify.app/press/">press kit</a> has the
          facts, screenshots and the trailer.
        </em>
      </p>
    </>
  ),
};

export default post;
